import { useMemo, useState } from 'react'
import { motion } from 'motion/react'
import { ScreenShell } from '@/components/layout/ScreenShell'
import { GameButton } from '@/components/ui/GameButton'
import { PixelIcon } from '@/components/ui/PixelIcon'
import { useProgress } from '@/contexts/ProgressProvider'
import { computeDailyStatus, claimDaily, DAILY_REWARDS } from '@/game/daily'
import { Haptics } from '@/arcade'
import { useGameAudio } from '@/contexts/GameAudioProvider'
import { ArtImage } from '@/components/ui/ArtImage'
import { CURRENCY_ART, productArt } from '@/game/art'

export function DailyRewardScreen() {
  const { save, update } = useProgress()
  const audio = useGameAudio()
  const [claimedNow, setClaimedNow] = useState(false)

  const status = useMemo(() => computeDailyStatus(save.meta.daily, Date.now()), [save.meta.daily])

  const claim = () => {
    if (!status.canClaim) return
    audio.play('levelup')
    if (save.meta.settings.haptics) Haptics.success()
    update((s) => claimDaily(s, Date.now()))
    setClaimedNow(true)
  }

  return (
    <ScreenShell title="Daily Gift">
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-3 rounded-[var(--r-lg)] bg-[var(--surface)] shadow-emboss border border-[var(--edge)]/40 p-4">
          <span className="grid h-12 w-12 shrink-0 place-items-center rounded-[var(--r-md)] bg-[var(--surface-raised)] shadow-emboss text-[var(--ember)]">
            <PixelIcon name="calendar" size={24} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="font-display text-lg font-black leading-tight">Day {save.meta.daily.streakDay || 0} of 7</div>
            <p className="text-xs text-[var(--text-faint)] leading-tight">Claim daily — miss one and the streak resets.</p>
          </div>
          <div className="flex shrink-0 gap-1">
            {DAILY_REWARDS.map((r) => (
              <span
                key={r.day}
                className="h-2 w-2 rounded-full"
                style={{ background: r.day <= (save.meta.daily.streakDay || 0) ? 'var(--ember)' : 'var(--edge)' }}
              />
            ))}
          </div>
        </div>

        <div className="grid grid-cols-4 gap-2">
          {DAILY_REWARDS.map((r) => {
            const isNext = status.canClaim && r.day === status.nextDay
            const isClaimed = r.day <= save.meta.daily.streakDay
            return (
              <motion.div
                key={r.day}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: r.day * 0.05 }}
                className={[
                  'flex flex-col items-center gap-1 rounded-[var(--r-md)] p-2 text-center shadow-emboss border',
                  isNext ? 'border-[var(--ember)] bg-[var(--surface-raised)]' : 'border-[var(--edge)]/30 bg-[var(--surface)]',
                  isClaimed && !isNext ? 'opacity-50' : '',
                  r.day === 7 ? 'col-span-2' : '',
                ].join(' ')}
              >
                <span className="text-[10px] font-bold text-[var(--text-faint)]">DAY {r.day}</span>
                <ArtImage
                  src={r.day === 7 ? productArt('starter_bundle') ?? CURRENCY_ART.chest : r.gems > 0 ? CURRENCY_ART.gem : CURRENCY_ART.coin}
                  alt={r.day === 7 ? 'Bonus chest' : r.gems > 0 ? 'Gems' : 'Coins'}
                  fallbackIcon={r.day === 7 ? 'gift' : r.gems > 0 ? 'gem' : 'coin'}
                  fallbackColor="var(--coin)"
                  className="h-10 w-10"
                  fit="contain"
                />
                <span className="text-xs font-display font-bold tabular-nums">
                  {r.coins}c{r.gems > 0 ? ` +${r.gems}g` : ''}
                </span>
                {r.lives ? <span className="text-[10px] text-[var(--text-faint)]">+{r.lives} life</span> : null}
                {r.powerups ? <span className="text-[10px] text-[var(--text-faint)]">+shield</span> : null}
                {isClaimed && <PixelIcon name="success" size={12} />}
              </motion.div>
            )
          })}
        </div>

        <GameButton size="lg" icon="gift" fullWidth silent disabled={!status.canClaim} onClick={claim}>
          {status.canClaim
            ? `Claim Day ${status.nextDay}`
            : claimedNow
              ? 'Claimed! Come back tomorrow'
              : 'Already claimed today'}
        </GameButton>
      </div>
    </ScreenShell>
  )
}
